import React from 'react';
// eslint-disable-next-line no-unused-vars
import { motion } from 'framer-motion';
import { UserX } from 'lucide-react';
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import CommonLoader from '../CommonLoader';

const FALLBACK_BAR_COLOR = '#EF4444';

// Shared tooltip for both vertical no-show bar charts. `labelKey` selects which
// field on the row holds the human-readable label (specialtyName | type).
const NoShowTooltip = ({ active, payload, labelKey }) => {
  if (active && payload && payload.length) {
    const row = payload[0]?.payload || {};
    const label = row?.[labelKey] || '';
    const percent = row?.percent ?? 0;
    const missed = row?.missed ?? 0;
    const total = row?.total ?? 0;
    return (
      <div
        data-testid="no-show-analytics-tooltip"
        className="bg-white p-2 border border-gray-200 rounded shadow-lg"
      >
        <p className="text-sm font-semibold text-gray-900 capitalize">
          {label}: {percent}% ({missed.toLocaleString()}/{total.toLocaleString()})
        </p>
      </div>
    );
  }
  return null;
};

const NoShowAnalytics = ({ dateRange, selectedPeriod, specialtyId, addressTypeName, noShowAnalytics, loading }) => {
  const { t } = useTranslation('dashboard');

  const noShowLoading = loading;

  const general = noShowAnalytics?.general || {};
  const generalPercent = general?.percent ?? 0;
  const generalCount = general?.count ?? 0;
  const generalTotal = general?.total ?? 0;

  const bySpecialty = Array.isArray(noShowAnalytics?.bySpecialty) ? noShowAnalytics.bySpecialty : [];
  const byType = Array.isArray(noShowAnalytics?.byType) ? noShowAnalytics.byType : [];

  const hasNoData = bySpecialty.length === 0 && byType.length === 0;

  return (
    <motion.div
      data-testid="no-show-analytics"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.4 }}
      className="bg-white rounded-xl p-4 border border-gray-200/50 shadow-md"
    >
      <div data-testid="no-show-analytics-header" className="mb-4">
        <h3
          data-testid="no-show-analytics-title"
          className="text-base sm:text-lg font-bold text-gray-900 flex items-center"
        >
          <UserX className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-[#4ABDBD]" />
          {t('dashboard.no_show_analytics', 'No-show analytics')}
        </h3>
      </div>

      {noShowLoading ? (
        <div
          data-testid="no-show-analytics-loading"
          className="flex flex-col items-center justify-center py-12"
        >
          <CommonLoader />
        </div>
      ) : hasNoData ? (
        <div
          data-testid="no-show-analytics-empty"
          className="flex flex-col items-center justify-center py-12"
        >
          <UserX data-testid="no-show-analytics-empty-icon" className="w-12 h-12 text-gray-300 mb-2" />
          <p data-testid="no-show-analytics-empty-text" className="text-sm text-gray-600">
            {t('dashboard.no_no_show_data', 'No no-show data')}
          </p>
        </div>
      ) : (
        <>
          <div
            data-testid="no-show-analytics-summary"
            className="rounded-lg bg-red-50 border border-red-100 p-3 mb-4"
          >
            <p className="text-sm font-semibold text-red-700">
              {t('dashboard.general_no_show_rate', 'General no-show rate')}: {generalPercent}% ({generalCount.toLocaleString()}/{generalTotal.toLocaleString()})
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div data-testid="no-show-analytics-by-specialty">
              <p className="text-sm font-semibold text-gray-700 mb-2">
                {t('dashboard.no_show_by_specialty', 'No-show by specialty')}
              </p>
              {bySpecialty.length > 0 ? (
                <div className="h-56 sm:h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      layout="vertical"
                      data={bySpecialty}
                      margin={{ top: 4, right: 12, bottom: 4, left: 0 }}
                    >
                      <XAxis
                        type="number"
                        domain={[0, 'dataMax']}
                        tickFormatter={(v) => `${v}%`}
                        tick={{ fontSize: 11 }}
                      />
                      <YAxis
                        type="category"
                        dataKey="specialtyName"
                        width={90}
                        tick={{ fontSize: 11 }}
                      />
                      <Tooltip
                        cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                        content={<NoShowTooltip labelKey="specialtyName" />}
                      />
                      <Bar dataKey="percent" radius={[0, 6, 6, 0]}>
                        {bySpecialty.map((row, index) => (
                          <Cell
                            key={`specialty-cell-${row?.specialtyId ?? index}`}
                            fill={row?.colour || FALLBACK_BAR_COLOR}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-56 sm:h-64 flex items-center justify-center">
                  <p className="text-sm text-gray-400">
                    {t('dashboard.no_no_show_data', 'No no-show data')}
                  </p>
                </div>
              )}
            </div>

            <div data-testid="no-show-analytics-by-type">
              <p className="text-sm font-semibold text-gray-700 mb-2">
                {t('dashboard.no_show_rate_by_type', 'No-show rate by appointment type')}
              </p>
              {byType.length > 0 ? (
                <div className="h-56 sm:h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      layout="vertical"
                      data={byType}
                      margin={{ top: 4, right: 12, bottom: 4, left: 0 }}
                    >
                      <XAxis
                        type="number"
                        domain={[0, 'dataMax']}
                        tickFormatter={(v) => `${v}%`}
                        tick={{ fontSize: 11 }}
                      />
                      <YAxis
                        type="category"
                        dataKey="type"
                        width={110}
                        tick={{ fontSize: 11 }}
                      />
                      <Tooltip
                        cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                        content={<NoShowTooltip labelKey="type" />}
                      />
                      <Bar dataKey="percent" fill={FALLBACK_BAR_COLOR} radius={[0, 6, 6, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-56 sm:h-64 flex items-center justify-center">
                  <p className="text-sm text-gray-400">
                    {t('dashboard.no_no_show_data', 'No no-show data')}
                  </p>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </motion.div>
  );
};

export default NoShowAnalytics;
